import { useState } from 'react';
import { X, Crown, Check, Sparkles } from 'lucide-react';
import { useSubscription, PRO_FEATURES, FEATURE_INFO } from '../../hooks/useSubscription';
import Modal from '../Modal';

export default function PaywallModal({ isOpen, onClose, feature = null }) {
  const {
    canStartTrial,
    startFreeTrial,
    openCheckout,
    isLoading,
    error,
  } = useSubscription();

  const [selectedPlan, setSelectedPlan] = useState('annual');

  const handleStartTrial = async () => {
    try {
      await startFreeTrial();
      onClose();
    } catch (err) {
      console.error('Failed to start trial:', err);
    }
  };

  const handleSubscribe = async () => {
    try {
      await openCheckout(selectedPlan);
    } catch (err) {
      console.error('Failed to open checkout:', err);
    }
  };

  const featureList = Object.values(PRO_FEATURES).map(key => FEATURE_INFO[key]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-0 right-0 p-2 text-text-tertiary hover:text-text-secondary transition-colors"
        >
          <X size={20} />
        </button>

        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="text-center space-y-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-accent/20 to-orange-500/20">
              <Crown className="w-8 h-8 text-accent" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-text-primary">Unlock Helm Pro</h2>
              <p className="text-text-secondary mt-1">Get full access to all productivity features</p>
            </div>
          </div>

          {/* Feature that triggered paywall */}
          {feature && FEATURE_INFO[feature] && (
            <div className="flex items-center gap-4 p-4 bg-surface-card rounded-xl border border-border-default">
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                <Crown className="w-5 h-5 text-accent" />
              </div>
              <div>
                <p className="text-xs text-text-secondary">You're trying to access</p>
                <p className="font-semibold text-text-primary">{FEATURE_INFO[feature].name}</p>
              </div>
            </div>
          )}

          {/* All Pro Features */}
          <div className="space-y-3">
            <h3 className="font-semibold text-text-primary">Everything in Pro</h3>
            <div className="grid grid-cols-1 gap-2">
              {featureList.map((feat, index) => (
                <div key={index} className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-success" />
                  <span className="text-sm text-text-primary">{feat.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing Plans */}
          <div className="space-y-3">
            {/* Annual Plan */}
            <button
              onClick={() => setSelectedPlan('annual')}
              className={`w-full p-4 rounded-xl border-2 transition-all ${
                selectedPlan === 'annual'
                  ? 'border-accent bg-accent/5'
                  : 'border-border-default hover:border-border-hover'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="text-left">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-text-primary">Annual</span>
                    <span className="px-2 py-0.5 text-xs font-bold text-white bg-success rounded-full">
                      BEST VALUE
                    </span>
                  </div>
                  <p className="text-sm text-text-secondary">Billed as $96/year</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-text-primary">$8/month</p>
                  <p className="text-xs text-success">Save $24/year</p>
                </div>
              </div>
            </button>

            {/* Monthly Plan */}
            <button
              onClick={() => setSelectedPlan('monthly')}
              className={`w-full p-4 rounded-xl border-2 transition-all ${
                selectedPlan === 'monthly'
                  ? 'border-accent bg-accent/5'
                  : 'border-border-default hover:border-border-hover'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="text-left">
                  <span className="font-semibold text-text-primary">Monthly</span>
                  <p className="text-sm text-text-secondary">Billed monthly</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-text-primary">$10/month</p>
                </div>
              </div>
            </button>
          </div>

          {/* Trial CTA */}
          {canStartTrial && (
            <div className="text-center space-y-1">
              <div className="flex items-center justify-center gap-2 text-accent">
                <Sparkles className="w-4 h-4" />
                <span className="font-medium">Start with a 14-day free trial</span>
              </div>
              <p className="text-xs text-text-secondary">No credit card required</p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="p-3 bg-error/10 border border-error/20 rounded-lg text-sm text-error">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {canStartTrial && (
              <>
                <button
                  onClick={handleStartTrial}
                  disabled={isLoading}
                  className="w-full py-3 px-4 bg-gradient-to-r from-accent to-orange-500 text-white font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {isLoading ? 'Starting...' : 'Start Free Trial'}
                </button>
                <div className="text-center text-xs text-text-tertiary">or</div>
              </>
            )}
            <button
              onClick={handleSubscribe}
              disabled={isLoading}
              className={`w-full py-3 px-4 font-medium rounded-xl transition-all ${
                canStartTrial
                  ? 'bg-accent/10 text-accent hover:bg-accent/20'
                  : 'bg-gradient-to-r from-accent to-orange-500 text-white hover:opacity-90'
              } disabled:opacity-50`}
            >
              {isLoading ? 'Loading...' : `Subscribe ${selectedPlan === 'annual' ? '$8/month' : '$10/month'}`}
            </button>
          </div>

          {/* Terms */}
          <p className="text-center text-xs text-text-tertiary">
            Subscription auto-renews. Cancel anytime.{' '}
            <a href="https://helmproductivity.com/legal/terms" className="text-text-secondary hover:underline">
              Terms
            </a>{' '}
            and{' '}
            <a href="https://helmproductivity.com/legal/privacy" className="text-text-secondary hover:underline">
              Privacy
            </a>
          </p>
        </div>
      </div>
    </Modal>
  );
}
